import { useContext, useState, useEffect, useRef, useMemo } from "react";
import { API, Auth, Hub } from "aws-amplify";
import { UserAdminContext } from "components/UserAdmin/UserAdmin-container";
import { useHistory } from "react-router-dom";
import useProfileId from "hooks/useProfile";
import { logError } from "util/logging";

const TIMEOUT_MS = 15000;

function raceWithTimeout(promise, ms, label = "API.get") {
  let t;
  const timeout = new Promise((_, rej) => {
    t = setTimeout(() => rej(new Error(`${label} timed out after ${ms}ms`)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(t));
}

const useUserAdminAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const history = useHistory();
  const { userProfile } = useContext(UserAdminContext);
  const { profileId } = useProfileId();

  const [ready, setReady] = useState(false);

  // overlap/stale-run control
  const inFlight = useRef(false);
  const runSeq   = useRef(0);

  const addNewUser = () => history.push(`/profileedit`);
  const addNewAccount = () => history.push(`/account`);

  // Stable signature for userProfile.accounts to avoid effect storms
  const userProfileAccountsSig = useMemo(() => {
    const ids = Array.isArray(userProfile?.accounts)
      ? userProfile.accounts.map(a => a?.id).filter(Boolean)
      : [];
    ids.sort();
    return ids.join("|");
  }, [userProfile?.accounts]);

  // Auth readiness + Hub
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (mounted) setReady(true);
      } catch {
        if (mounted) setReady(false);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try {
          await Auth.currentAuthenticatedUser();
          if (mounted) setReady(true);
        } catch {
          if (mounted) setReady(false);
        }
      } else if (evt === "signOut") {
        if (mounted) {
          setReady(false);
          setAccounts([]);
        }
      }
    });

    return () => { mounted = false; unsub(); };
  }, []);

  // Fetch accounts
  useEffect(() => {
    if (!ready) { setLoading(false); return; }
    if (!profileId) { setLoading(false); return; }

    const myRun = ++runSeq.current;
    inFlight.current = true;
    setLoading(true);

    let cancelled = false;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser();

        let raw = await raceWithTimeout(
          API.get("AccountGatewayApi", "/accounts"),
          TIMEOUT_MS,
          "AccountGatewayApi /accounts"
        );

        if (typeof raw === "string") {
          try { raw = JSON.parse(raw); } catch { /* keep as-is */ }
        }

        const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.accounts) ? raw.accounts : []);
        const pageUserAccounts = Array.isArray(userProfile?.accounts) ? userProfile.accounts : [];

        const mapped = [];
        for (let i = 0; i < (Array.isArray(list) ? list.length : 0); i++) {
          const account = list[i];
          try {
            const base = {
              accountId: account?.id ?? "",
              name:      account?.name ?? "",
              brand:     typeof account?.brand === "string" ? account.brand : (account?.brand?.name ?? ""),
              role: "",
              accountProfileId: userProfile?.id ?? ""
            };
            const match = pageUserAccounts.find(a => a?.id === account?.id);
            if (match?.role) base.role = match.role;

            // Optional safety: skip obviously unsafe names
            if (typeof base.name === "string" && (base.name.includes("<") || base.name.includes(">"))) {
              continue;
            }

            mapped.push(base);
          } catch {
            // skip bad item, continue
          }
        }

        if (!cancelled && myRun === runSeq.current) {
          setAccounts(mapped);
          setError(null);
        }
      } catch (err) {
        logError?.("useUserAdminAccounts fetch error", err);
        if (!cancelled && myRun === runSeq.current) {
          setError(err);
          setAccounts([]);
        }
      } finally {
        inFlight.current = false;
        if (!cancelled && myRun === runSeq.current) {
          setLoading(false);
        }
      }
    })();

    // Cleanup: if cancelling the *current* run, drop loading so UI doesn’t stick
    return () => {
      cancelled = true;
      if (myRun === runSeq.current) {
        inFlight.current = false;
        setLoading(false);
      }
    };
  }, [ready, profileId, userProfile?.id, userProfileAccountsSig]);

  return {
    accounts,
    loading,
    error,
    addNewUser,
    addNewAccount
  };
};

export default useUserAdminAccounts;
